// Follow Horizon's native variant events without changing its picker or cart.
class ArtsySelectedFitNote extends HTMLElement {
  #controller;
  #pendingSelection;

  #writeFacts = (facts) => {
    const fields = ['style', 'model', 'size', 'color', 'width', 'length'];
    const nodes = fields.map((field) => this.querySelector(`[data-fit-note-${field}]`));
    if (nodes.some((node) => !node) || fields.some((field) => !facts[field])) return false;
    nodes.forEach((node, index) => { node.textContent = facts[fields[index]]; });
    return true;
  };

  #selectedFacts = (picker, changedId) => {
    const selected = [...picker.querySelectorAll('input[type="radio"]:checked, select option:checked')];
    const choices = this.querySelector('template[data-fit-note-choices]')?.content;
    const rows = this.querySelector('template[data-fit-note-rows]')?.content;
    const changed = selected.find((input) => input.dataset.optionValueId === String(changedId));
    if (!choices || !rows || selected.length !== 3 || changed?.dataset.optionAvailable !== 'true') return;
    const facts = {}, ids = selected.map((input) => input.dataset.optionValueId);
    if (new Set(ids).size !== 3 || ids.some((id) => !id)) return;
    for (const input of selected) {
      const matches = [...choices.querySelectorAll('[data-option-value-id]')]
        .filter((choice) => choice.dataset.optionValueId === input.dataset.optionValueId);
      const choice = matches[0]?.dataset;
      if (matches.length !== 1 || !['Style', 'Size', 'Color'].includes(choice.fitName) ||
          choice.fitValue !== input.value || facts[choice.fitName.toLowerCase()]) return;
      facts[choice.fitName.toLowerCase()] = choice.fitValue;
    }
    const matches = [...rows.querySelectorAll('[data-fit-style]')]
      .filter((row) => row.dataset.fitStyle === facts.style && row.dataset.fitSize === facts.size);
    if (matches.length !== 1) return;
    const row = matches[0].dataset;
    return { ...facts, model: row.fitModel, width: row.fitWidth, length: row.fitLength, ids: ids.join(',') + ',' };
  };

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const section = this.closest('.shopify-section');
    if (!section) return;
    const options = { signal: this.#controller.signal };
    section.addEventListener('variant:selected', this.#selectionPending, options);
    section.addEventListener('variant:update', this.#variantUpdated, options);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #showFallback = () => {
    const fallback = this.querySelector('template[data-fit-note-fallback]');
    const content = this.querySelector('[data-fit-note-content]');
    if (fallback && content) content.replaceChildren(fallback.content.cloneNode(true));
    this.dataset.variantId = '';
    this.#pendingSelection = undefined;
    delete this.dataset.fitSelection;
    delete this.dataset.artsyPreviewContext;
    this.removeAttribute('aria-busy');
  };

  #selectionPending = (event) => {
    const picker = event.target.closest?.('variant-picker');
    if (picker?.dataset.productId !== this.dataset.productId) return;
    const selectedId = event.detail?.resource?.id;
    const facts = this.#selectedFacts(picker, selectedId);
    this.#pendingSelection = facts?.ids;
    // These are exact chart facts for the selected native option IDs, not availability.
    if (facts && this.#writeFacts(facts)) {
      this.dataset.variantId = '';
      this.dataset.fitSelection = facts.ids;
      delete this.dataset.artsyPreviewContext;
      this.removeAttribute('aria-busy');
      return;
    }
    const option = [...picker.querySelectorAll('[data-option-value-id]')]
      .find((input) => input.dataset.optionValueId === String(selectedId));
    const context = option?.dataset.artsyPreviewContext;
    const contextIds = context?.split(',').filter(Boolean) || [];
    const selected = [...picker.querySelectorAll('input[type="radio"]:checked, select option:checked')]
      .map((input) => input.dataset.optionValueId);
    const color = this.querySelector('[data-fit-note-color]');
    // The same native Style/Size IDs still describe the visible measurements.
    if (!this.hasAttribute('aria-busy') && this.dataset.variantId && color &&
        option?.matches('input:checked, option:checked') && option.hasAttribute('data-artsy-color-preview') &&
        option.dataset.optionAvailable === 'true' && context && context === this.dataset.artsyPreviewContext &&
        contextIds.length === 2 && new Set(contextIds).size === 2 &&
        contextIds.every((id) => selected.includes(id))) {
      color.textContent = option.value;
      return;
    }
    this.dataset.variantId = '';
    this.setAttribute('aria-busy', 'true');
  };

  #variantUpdated = (event) => {
    const detail = event.detail;
    if (String(detail?.data?.productId) !== this.dataset.productId) return;
    const variantId = detail.resource?.id;
    const html = detail.data?.html;
    if (!variantId || !html?.querySelectorAll) {
      this.#showFallback();
      return;
    }

    const next = Array.from(html.querySelectorAll('artsy-selected-fit-note')).find(
      (note) => note.dataset.productId === this.dataset.productId && note.dataset.blockId === this.dataset.blockId
    );
    const nextContent = next?.querySelector('[data-fit-note-content]');
    const content = this.querySelector('[data-fit-note-content]');
    if (!nextContent || !content || next.dataset.variantId !== String(variantId)) {
      this.#showFallback();
      return;
    }

    // A late response must not put an earlier selection's measurements back.
    const picker = event.target.closest?.('variant-picker');
    const selectedIds = [...(picker?.querySelectorAll('input[type="radio"]:checked, select option:checked') || [])]
      .map((input) => input.dataset.optionValueId);
    const currentSelection = selectedIds.length === 3 && selectedIds.every(Boolean) ? selectedIds.join(',') + ',' : undefined;
    const knownChoices = [...(this.querySelector('template[data-fit-note-choices]')?.content.querySelectorAll('[data-option-value-id]') || [])];
    const currentIdsKnown = selectedIds.every((id) => knownChoices.some((choice) => choice.dataset.optionValueId === id));
    if ((this.#pendingSelection && next.dataset.fitSelection !== this.#pendingSelection) ||
        (next.dataset.fitSelection && currentSelection && currentIdsKnown && next.dataset.fitSelection !== currentSelection)) return;

    const facts = Object.fromEntries(['style', 'model', 'size', 'color', 'width', 'length']
      .map((field) => [field, nextContent.querySelector(`[data-fit-note-${field}]`)?.textContent]));

    const color = content.querySelector('[data-fit-note-color]');
    const nextColor = nextContent.querySelector('[data-fit-note-color]');
    const sameMeasurements = ['.artsy-selected-fit-note__garment', '.artsy-selected-fit-note__dimensions']
      .every((selector) => {
        const current = content.querySelector(selector), incoming = nextContent.querySelector(selector);
        return current && incoming && current.textContent === incoming.textContent;
      });
    if (this.#writeFacts(facts)) {
      // Keep the measurement paragraphs and the focused fit-guide link in place.
    } else if (color && nextColor && sameMeasurements && this.dataset.artsyPreviewContext &&
        this.dataset.artsyPreviewContext === next.dataset.artsyPreviewContext) {
      color.textContent = nextColor.textContent;
    } else {
      content.replaceChildren(...Array.from(nextContent.childNodes, (node) => node.cloneNode(true)));
    }
    this.dataset.variantId = String(variantId);
    this.dataset.artsyPreviewContext = next.dataset.artsyPreviewContext || '';
    this.dataset.fitSelection = next.dataset.fitSelection || '';
    this.#pendingSelection = undefined;
    this.removeAttribute('aria-busy');
  };
}

if (!customElements.get('artsy-selected-fit-note')) {
  customElements.define('artsy-selected-fit-note', ArtsySelectedFitNote);
}
